/**
 * Purpose: Small status pill (verified, open/closed, featured, availability…).
 * Inputs: variant ('verified'|'open'|'closed'|'featured'|'available'|'unavailable'|
 *         'brand'|'neutral'), size ('sm'|'md'), icon override (node), children (label).
 * Outputs: inline <span>; verified/open/closed ship a default icon.
 */
import React from 'react';

const VARIANT_CLASSES = {
  verified: 'bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/20 dark:bg-brand-900 dark:text-brand-200',
  open: 'bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-200',
  closed: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  featured: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  available: 'bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-200',
  unavailable: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  brand: 'bg-brand-600 text-white',
  neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const SIZE_CLASSES = {
  sm: 'px-2 py-0.5 text-[11px] gap-1',
  md: 'px-2.5 py-1 text-xs gap-1.5',
};

const DEFAULT_ICONS = {
  verified: (
    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M16.403 12.652a3 3 0 000-5.304 3 3 0 00-3.75-3.751 3 3 0 00-5.305 0 3 3 0 00-3.751 3.75 3 3 0 000 5.305 3 3 0 003.75 3.751 3 3 0 005.305 0 3 3 0 003.751-3.75zm-2.546-4.46a.75.75 0 00-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
        clipRule="evenodd"
      />
    </svg>
  ),
  open: <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />,
  closed: <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />,
};

const Badge = ({ variant = 'neutral', size = 'md', icon, className = '', children }) => {
  const resolvedIcon = icon !== undefined ? icon : DEFAULT_ICONS[variant] || null;
  return (
    <span
      className={[
        'inline-flex items-center rounded-full font-medium whitespace-nowrap',
        VARIANT_CLASSES[variant] || VARIANT_CLASSES.neutral,
        SIZE_CLASSES[size] || SIZE_CLASSES.md,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {resolvedIcon}
      {children}
    </span>
  );
};

export default Badge;
